/**
 * Crew Member Template bind — Space-scoped TEMPLATE Members (Run 3)
 *
 * Persists:
 * 1. Standing mandate → spaces.custom_instructions (C1 injection path)
 * 2. Binding marker → spaces.display_preferences.crew_member_template_bind
 *
 * Does NOT invent Helper rows or flip C3 Helper bindStatus off NOT_BOUND_YET.
 *
 * Preserve LosiAnimated. No Muse/Vesper.
 */

import type { MateId } from '@/lib/crew/mates'
import { crewMemberIdentityFromTemplate } from '@/lib/crew/mate-identity'
import {
  composeCrewMemberTemplateStandingInstructions,
  getCrewMemberTemplate,
  parseCrewMemberTemplateIdFromInstructions,
  type CrewMemberTemplate,
  type CrewMemberTemplateId,
  type CrewMemberTemplateToolPolicy
} from '@/lib/crew/member-templates'

export const CREW_MEMBER_TEMPLATE_BIND_STATUS_BOUND = 'TEMPLATE_BOUND' as const
export const CREW_MEMBER_TEMPLATE_BIND_STATUS_UNBOUND = 'UNBOUND' as const

export type CrewMemberTemplateBindStatus =
  | typeof CREW_MEMBER_TEMPLATE_BIND_STATUS_BOUND
  | typeof CREW_MEMBER_TEMPLATE_BIND_STATUS_UNBOUND

/** Stored under spaces.display_preferences */
export const CREW_MEMBER_TEMPLATE_BIND_PREFS_KEY =
  'crew_member_template_bind' as const

export type CrewMemberTemplateBinding = {
  status: typeof CREW_MEMBER_TEMPLATE_BIND_STATUS_BOUND
  templateId: CrewMemberTemplateId
  mateId: MateId
  displayName: string
  spaceId: string
  boundAt: string
  toolPolicy: CrewMemberTemplateToolPolicy
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

export function normalizeCrewMemberTemplateBinding(
  raw: unknown
): CrewMemberTemplateBinding | null {
  if (!isRecord(raw)) return null
  if (raw.status !== CREW_MEMBER_TEMPLATE_BIND_STATUS_BOUND) return null
  const template = getCrewMemberTemplate(
    typeof raw.templateId === 'string'
      ? raw.templateId
      : typeof raw.template_id === 'string'
        ? raw.template_id
        : null
  )
  if (!template) return null
  const spaceId =
    typeof raw.spaceId === 'string'
      ? raw.spaceId
      : typeof raw.space_id === 'string'
        ? raw.space_id
        : template.defaultSpaceId
  const boundAt =
    typeof raw.boundAt === 'string'
      ? raw.boundAt
      : typeof raw.bound_at === 'string'
        ? raw.bound_at
        : new Date(0).toISOString()

  return {
    status: CREW_MEMBER_TEMPLATE_BIND_STATUS_BOUND,
    templateId: template.id,
    mateId: template.mateId,
    displayName:
      typeof raw.displayName === 'string' && raw.displayName.trim()
        ? raw.displayName.trim().slice(0, 40)
        : template.displayName,
    spaceId,
    boundAt,
    toolPolicy: template.toolPolicy
  }
}

export function extractCrewMemberTemplateBinding(
  displayPreferences: unknown
): CrewMemberTemplateBinding | null {
  if (!isRecord(displayPreferences)) return null
  return normalizeCrewMemberTemplateBinding(
    displayPreferences[CREW_MEMBER_TEMPLATE_BIND_PREFS_KEY]
  )
}

export function mergeCrewMemberTemplateBinding(
  existingDisplayPreferences: unknown,
  binding: CrewMemberTemplateBinding | null
): Record<string, unknown> {
  const base: Record<string, unknown> = isRecord(existingDisplayPreferences)
    ? { ...existingDisplayPreferences }
    : {}

  if (binding == null) {
    delete base[CREW_MEMBER_TEMPLATE_BIND_PREFS_KEY]
    return base
  }

  base[CREW_MEMBER_TEMPLATE_BIND_PREFS_KEY] = {
    status: CREW_MEMBER_TEMPLATE_BIND_STATUS_BOUND,
    templateId: binding.templateId,
    mateId: binding.mateId,
    displayName: binding.displayName,
    spaceId: binding.spaceId,
    boundAt: binding.boundAt,
    toolPolicy: binding.toolPolicy
  }
  return base
}

export function buildCrewMemberTemplateBinding(input: {
  template: CrewMemberTemplate
  spaceId?: string
  boundAt?: string
  displayName?: string
}): CrewMemberTemplateBinding {
  return {
    status: CREW_MEMBER_TEMPLATE_BIND_STATUS_BOUND,
    templateId: input.template.id,
    mateId: input.template.mateId,
    displayName: input.displayName?.trim() || input.template.displayName,
    spaceId: input.spaceId || input.template.defaultSpaceId,
    boundAt: input.boundAt || new Date().toISOString(),
    toolPolicy: input.template.toolPolicy
  }
}

/**
 * Payload to PUT /api/spaces/:id (or service-role update) for a template bind.
 */
export function buildCrewMemberTemplateSpaceUpdate(input: {
  template: CrewMemberTemplate
  spaceId?: string
  existingCustomInstructions?: string | null
  existingDisplayPreferences?: unknown
  boundAt?: string
}): {
  custom_instructions: string
  display_preferences: Record<string, unknown>
  binding: CrewMemberTemplateBinding
} {
  const binding = buildCrewMemberTemplateBinding({
    template: input.template,
    spaceId: input.spaceId,
    boundAt: input.boundAt
  })

  // If instructions already carry this template, don't double-wrap extras.
  const existingId = parseCrewMemberTemplateIdFromInstructions(
    input.existingCustomInstructions
  )
  const preserveExtra =
    existingId === input.template.id
      ? null
      : stripTemplateMarkerBlock(input.existingCustomInstructions)

  return {
    custom_instructions: composeCrewMemberTemplateStandingInstructions(
      input.template,
      { preserveExtra }
    ),
    display_preferences: mergeCrewMemberTemplateBinding(
      input.existingDisplayPreferences,
      binding
    ),
    binding
  }
}

function stripTemplateMarkerBlock(
  customInstructions: string | null | undefined
): string | null {
  if (typeof customInstructions !== 'string') return null
  const trimmed = customInstructions.trim()
  if (!trimmed) return null
  if (!/<!--\s*losi-crew-member-template:/i.test(trimmed)) return trimmed
  // Drop prior template compose; keep human extras after the sentinel.
  const sentinel = 'ADDITIONAL SPACE NOTES:'
  const idx = trimmed.indexOf(sentinel)
  if (idx < 0) return null
  const extra = trimmed.slice(idx + sentinel.length).trim()
  return extra || null
}

export function resolveBoundCrewMemberTemplate(input: {
  displayPreferences?: unknown
  customInstructions?: string | null
}): CrewMemberTemplate | null {
  const fromPrefs = extractCrewMemberTemplateBinding(input.displayPreferences)
  if (fromPrefs) return getCrewMemberTemplate(fromPrefs.templateId)
  const fromInstructions = parseCrewMemberTemplateIdFromInstructions(
    input.customInstructions
  )
  return fromInstructions ? getCrewMemberTemplate(fromInstructions) : null
}

/** Roster seat for Crew Chat / Your-crew chrome from a template. */
export function crewMemberTemplateToRosterSeat(template: CrewMemberTemplate): {
  mateId: MateId
  displayName: string
  role: 'leader'
} {
  const { mateId, displayName } = crewMemberIdentityFromTemplate(template)
  return {
    mateId,
    displayName,
    role: 'leader'
  }
}
